import type { ExplorerPane } from 'types/explorer'
import type { Group } from 'types/group'
import type { SearchPageState, SourceScope } from 'types/search-page'
import type { SortingState } from 'types/data-table'
import type { FilterState } from 'types/filter-page'

import mixpanel from '@disco/api/mixpanel'

import {
  CUSTOM_CHANNEL,
  CUSTOM_INBOX,
  INBOX_TYPES,
  PERSONAL_INBOX,
  PERSONAL_LIBRARY,
  TEAM_INBOX,
  TEAM_LIBRARY,
} from '@disco/constants/GroupTypes'
import { SEARCH_RESULTS_PANE, TRACK_PANE } from '@disco/constants/PaneTypes'
import {
  SEARCH_MDS_DISPLAY_NAME,
  SEARCH_MY_DISCO_DISPLAY_NAME,
} from '@disco/constants/search'
import { hasFilterValue, isUntouchedOrBothScopes } from './search-page'

/**
 * Maps explorer pane data to Mixpanel analytics event objects.
 *
 * Converts explorer pane information into structured analytics events
 * for tracking user interactions with different pane types and group types.
 *
 * @param pane - Explorer pane object containing type and metadata
 * @param mixpanelContext - Additional context properties for analytics
 * @returns Mixpanel event object with event name and properties, or null if not trackable
 *
 * @example
 * ```typescript
 * // Track pane event
 * const trackPane = { type: 'TRACK_PANE', title: 'My Song.mp3' }
 * const trackEvent = mapPaneToMixpanelEventObject(trackPane)
 * // {
 * //   event: 'Open track pane in explorer',
 * //   props: { title: 'My Song.mp3' }
 * // }
 *
 * // Personal library event
 * const libraryPane = {
 *   type: 'SEARCH_RESULTS_PANE',
 *   groupType: 'PERSONAL_LIBRARY',
 *   name: 'My Library'
 * }
 * const libraryEvent = mapPaneToMixpanelEventObject(libraryPane)
 * // {
 * //   event: 'Open personal library in explorer',
 * //   props: { title: 'My Library' }
 * // }
 *
 * // Custom channel with context
 * const channelPane = {
 *   type: 'SEARCH_RESULTS_PANE',
 *   groupType: 'CUSTOM_CHANNEL',
 *   name: 'Rock Music'
 * }
 * const context = { channelId: 123 }
 * const channelEvent = mapPaneToMixpanelEventObject(channelPane, context)
 * // {
 * //   event: 'Open custom channel in explorer',
 * //   props: { title: 'Rock Music', channelId: 123 }
 * // }
 * ```
 */
export function mapPaneToMixpanelEventObject(
  pane: Partial<ExplorerPane>,
  mixpanelContext?: Record<string, any>,
):
  | {
      event: string
      props: {
        title: string
      }
    }
  | null
  | undefined {
  if (pane.type === TRACK_PANE) {
    return {
      event: 'Open track pane in explorer',
      props: {
        ...pane.additionalMixpanelProps,
        title: pane.title ?? '',
      },
    }
  } else if (pane.type === SEARCH_RESULTS_PANE) {
    switch (pane.groupType) {
      case PERSONAL_LIBRARY:
        return {
          event: 'Open personal library in explorer',
          props: { title: pane.name || '' },
        }
      case PERSONAL_INBOX:
        return {
          event: 'Open personal inbox in explorer',
          props: { title: pane.name || '' },
        }
      case TEAM_INBOX:
        return {
          event: 'Open team inbox in explorer',
          props: { title: pane.name || '' },
        }
      case CUSTOM_INBOX:
        return {
          event: 'Open custom inbox in explorer',
          props: { title: pane.name || '' },
        }
      case CUSTOM_CHANNEL:
        return {
          event: 'Open custom channel in explorer',
          props: { title: pane.name || '', ...mixpanelContext },
        }
      default:
        // FIXME: this no longer works, folders are being tracked as channels instead
        if (pane.groupId) {
          return {
            event: 'Open folder in explorer',
            props: { title: pane.name || '' },
          }
        }
    }
  }

  return null
}

/**
 * Maps the currently selected library to the sidebar section it represents,
 * for use as the `Context` property on row icon 'Click icon' events.
 * @param library - The currently selected library/group
 * @returns The sidebar section name, or 'Explorer' if it doesn't match a known section
 */
export function getRowIconMixpanelContext(
  library: Group | null | undefined,
): 'Browse' | 'Starred' | 'Inbox' | 'Channel' | 'Explorer' {
  if (!library) return 'Explorer'
  if (library.group_type === TEAM_LIBRARY) return 'Browse'
  if (library.group_type === PERSONAL_LIBRARY) return 'Starred'
  if (library.group_type === CUSTOM_CHANNEL) return 'Channel'
  if (INBOX_TYPES.includes(library.group_type)) return 'Inbox'
  return 'Explorer'
}

const getSearchLoadTime = (startTime: Date | null, endTime: Date | null) => {
  if (!startTime || !endTime) return null
  return endTime.getTime() - startTime.getTime()
}

export function sendSearchSortingEvent(
  sorting: SortingState,
  sortStartTime: Date,
  searchName: string,
): void {
  if (!sorting?.length) return
  const sortEndTime = new Date()
  const sortLoadTime = getSearchLoadTime(sortStartTime, sortEndTime)

  const sortingDescription = sorting
    .map(s => `${s.id} ${s.desc ? 'descending' : 'ascending'}`)
    .join(', ')

  mixpanel.sendPureEvent('Search Sorting', {
    'Sort order': sortingDescription,
    Time: sortLoadTime,
    Context: searchName,
  })
}

const getFilterValues = (
  filters: FilterState[] = [],
): FilterState[] | undefined => {
  const filtered = filters.filter(f => hasFilterValue(f.value))
  if (!filtered.length) return undefined
  return filtered
}

const SOURCE_SCOPE_LABELS: Record<SourceScope, string> = {
  business: SEARCH_MY_DISCO_DISPLAY_NAME,
  mds: SEARCH_MDS_DISPLAY_NAME, // 'Other Opted-In DISCOs' (Marketing term for Multi-DISCO Search)
}

// canonical Mixpanel ordering for multi-scope values; do not reorder
const SOURCE_SCOPE_ORDER: SourceScope[] = ['business', 'mds']

const getSourceScopeValue = (
  sourceScope?: SearchPageState['sourceScope'],
): string | undefined => {
  if (!sourceScope?.length) return undefined

  const orderedScopes = SOURCE_SCOPE_ORDER.filter(scope =>
    sourceScope.includes(scope),
  )

  if (!orderedScopes.length) return undefined

  return orderedScopes.map(scope => SOURCE_SCOPE_LABELS[scope]).join(', ')
}

export function sendMixpanelSearchEvent(
  searchState: Partial<SearchPageState>,
  searchStartTime: Date,
  searchName: string,
): void {
  const { query, filters, sourceScope } = searchState
  const appliedFilters = getFilterValues(filters)
  const shouldIncludeScope = !isUntouchedOrBothScopes(sourceScope)
  const scopeValue = shouldIncludeScope
    ? getSourceScopeValue(sourceScope)
    : undefined

  if (!query?.trim() && !appliedFilters) return

  const searchEndTime = new Date()
  const searchLoadTime = getSearchLoadTime(searchStartTime, searchEndTime)

  const updatedFilters = appliedFilters?.map(f => ({
    label: f.definition.label,
    value: f.value,
  }))

  const filtersDescription = updatedFilters?.length
    ? updatedFilters.map(f => f.label).join(', ')
    : null

  mixpanel.sendPureEvent('Search', {
    'Search Term': query,
    Filters: filtersDescription,
    ...(scopeValue !== undefined && { Scope: scopeValue }),
    Time: searchLoadTime,
    Context: searchName,
  })
}
